import { gql } from '@apollo/client';

import { assertUnreachable } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

export const LaunchStepStatusFragment = gql`
  fragment LaunchStepStatusFragment on Launch {
    id
    sequence {
      ... on LaunchSequenceInitiatedStep {
        startedAt
      }
      ... on LaunchSequenceBuildStep {
        startedAt
        completedAt
      }
      ... on LaunchSequencePublishStep {
        startedAt
        completedAt
      }
      ... on LaunchSequenceCompletedStep {
        completedAt
      }
    }
    # Used to determine build success/failure
    build {
      result {
        __typename
      }
    }
    # Used to determine check success/failure
    schemaTag {
      id
      diffToPrevious {
        severity
      }
    }
  }
`;

/**
 * For each step in a build pipeline, there's three normal states:
 * 1. Waiting to run
 * 2. Running
 * 3. Succeeded
 * And there's two exception states:
 * 4. Skipped
 *   - This could be due to prerequisite step failure.
 *   - This could be due to cancellation of the launch (e.g. superseding).
 * 5. Failed
 * Note that previous != prerequisite (a check step failing does not prevent the
 * subsequent publish step from occurring).
 *
 * When given a launch and a step, this function does two things:
 * - Categorizes a launch's "progress" to successfully completing the given step
 *   into three groups:
 *   - "waiting": The step has not yet completed (states 1 and 2)
 *   - "success": The step has completed successfully (state 3)
 *   - "failure": The step has completed exceptionally (states 4 and 5)
 * - Gives a phrase that describes the "status" of the latest step prerequisite
 *   (or equal) to the given step that is "Running", "Succeeded", or "Failed"
 *   (states 2, 3, or 5).
 *   - E.g. if the given step was "publish" but the build step failed, the
 *     "status" phrase would be "build failed" (as the check and publish steps
 *     would be skipped).
 * - Returns whether the given step has started (states 2, 3, or 5).
 */
export function stepStatus({
  launch,
  step,
}: {
  launch: GraphQLTypes.LaunchStepStatusFragment;
  step: 'build' | 'launch';
}): {
  status: string;
  progress: 'waiting' | 'success' | 'failure';
  isStarted: boolean;
} {
  let status: string = 'launch initiating';
  let isStarted: boolean = false;

  for (const sequenceStep of launch.sequence) {
    switch (sequenceStep.__typename) {
      case 'LaunchSequenceInitiatedStep': {
        const newStatus = sequenceStep.startedAt ? 'launch initiated' : null;
        status = newStatus ?? status;
        break;
      }
      case 'LaunchSequenceBuildStep': {
        const newStatus = sequenceStep.completedAt
          ? launch.build?.result?.__typename === 'BuildSuccess'
            ? 'built'
            : 'build failed'
          : sequenceStep.startedAt
            ? 'building'
            : null;
        status = newStatus ?? status;
        if (newStatus && step === 'build') {
          isStarted = true;
        }

        if (newStatus === 'build failed') {
          return {
            status,
            progress: 'failure',
            isStarted,
          };
        }
        if (step === 'build') {
          return {
            status,
            progress: newStatus === 'built' ? 'success' : 'waiting',
            isStarted,
          };
        }
        break;
      }
      case 'LaunchSequencePublishStep': {
        const newStatus = sequenceStep.completedAt
          ? 'published'
          : sequenceStep.startedAt
            ? 'publishing'
            : null;
        status = newStatus ?? status;
        break;
      }
      case 'LaunchSequenceCompletedStep': {
        const newStatus = sequenceStep.completedAt ? 'launched' : null;
        status = newStatus ?? status;
        if (newStatus && step === 'launch') {
          isStarted = true;
        }

        if (step === 'launch') {
          return {
            status,
            progress: newStatus === 'launched' ? 'success' : 'waiting',
            isStarted,
          };
        }
        break;
      }
      case 'LaunchSequenceSupersededStep': {
        // This union member is never returned by the backend.
        break;
      }
      default: {
        assertUnreachable(sequenceStep);
      }
    }
  }

  return {
    status,
    progress: 'waiting',
    isStarted,
  };
}
